import { gql } from 'graphql-tag';

export const meetingDetailsFragment = gql`
  fragment MeetingDetails on Meeting {
    title
    number
    numberTitle
    fullMeetingTitle
    iHaveSeenTheWholeMeeting
    startDate
    endDate
    meetingType
    procedureType
    status
    minutesStatus
    locationName
    quorum
    meetingLeader
    videoConfUrl
    preVotingEnabled
    filesSectionHidden
    hideVideoIcon
    hideChatIcon
    hideProxyForm
    isClosedForMoreParticipants
  }
`;

export const meetingsCalendarQuery = gql`
  query MeetingsForOrg($organizationId: ID!) {
    meetings(organizationId: $organizationId) {
      id
      title
      number
      numberTitle
      fullMeetingTitle
      iHaveSeenTheWholeMeeting
      startDate
      endDate
      meetingType
      procedureType
      status
      preVotingEnabled
      filesSectionHidden
      hideVideoIcon
      hideChatIcon
      hideProxyForm
      hasUnseenChatMessagesForUser
      hasUnreadEmailsForUser
      meetingCloneOf
      agendaItems {
        id
        title
        agendaStartingIndex
        agendaItemCategory
        hideAgendaNumbering
      }
    }
  }
`;

export const meetingUpdatesForCalendarSubscription = gql`
  subscription MeetingUpdatesForCalendar($organizationId: ID!) {
    meetingUpdatesForCalendar(organizationId: $organizationId) {
      organizationId
      meetingId
      deleted
      meeting {
        id
        ...MeetingDetails
        hasUnseenChatMessagesForUser
        meetingCloneOf
        hasUnreadEmailsForUser
        agendaItems {
          id
          title
          agendaStartingIndex
          agendaItemCategory
          hideAgendaNumbering
        }
      }
    }
  }
  ${meetingDetailsFragment}
`;

export const meetingDetailsQuery = gql`
  query Meeting($id: ID!) {
    meeting(id: $id) {
      id
      ...MeetingDetails
      nUnseenChatMessagesForUser
    }
  }
  ${meetingDetailsFragment}
`;

export const deleteMeetingMutation = gql`
  mutation DeleteMeeting($meetingIds: [ID]!) {
    deleteMeetings(meetingIds: $meetingIds) {
      deletedCount
    }
  }
`;

export const createMeetingMutation = gql`
  mutation CreateMeeting(
    $organizationId: ID!
    $input: [MeetingDetailsEditInput]!
    $initialAgendaItemSuggestionIds: [ID!]
  ) {
    createMeetings(
      organizationId: $organizationId
      input: $input
      initialAgendaItemSuggestionIds: $initialAgendaItemSuggestionIds
    ) {
      id
    }
  }
`;

export const editMeetingDetailsMutation = gql`
  mutation EditMeetingDetails($meetingId: ID!, $input: MeetingDetailsEditInput!) {
    editMeetingDetails(meetingId: $meetingId, input: $input) {
      id
      ...MeetingDetails
    }
  }
  ${meetingDetailsFragment}
`;

export const meetingGeneralCommentsQuery = gql`
  query MeetingGeneralComments($meetingId: ID!) {
    meeting(id: $meetingId) {
      id
      agendaGeneralComments
      minutesGeneralComments
      minutesGroupActiveConsolidated
      minutesGroupActiveSeparate
      minutesGroupReference
      minutesApprovals {
        id
        userId
        direntId
        createdAt
        updatedAt
      }
    }
  }
`;

export const updateAgendaGeneralCommentsMutation = gql`
  mutation UpdateAgendaGeneralComments($meetingId: ID!, $newValue: String!, $deviceId: String!) {
    updateAgendaGeneralComments(meetingId: $meetingId, newValue: $newValue, deviceId: $deviceId)
  }
`;

export const iHaveSeenSomethingInteresting = gql`
  mutation IHaveSeenSomethingInteresting(
    $meetingId: ID!
    $deviceId: String!
    $payload: IHaveSeenSomethingInterestingPayload!
  ) {
    iHaveSeenSomethingInteresting(meetingId: $meetingId, deviceId: $deviceId, payload: $payload)
  }
`;

export const updateMinutesGeneralCommentsMutation = gql`
  mutation UpdateMinutesGeneralComments($meetingId: ID!, $newValue: String!, $deviceId: String!) {
    updateMinutesGeneralComments(meetingId: $meetingId, newValue: $newValue, deviceId: $deviceId)
  }
`;

export const updateMinutesMassCreationSettingsMutation = gql`
  mutation UpdateMinutesMassCreationSettings(
    $meetingId: ID!
    $minutesGroupActiveConsolidated: Boolean
    $minutesGroupActiveSeparate: Boolean
    $minutesGroupReference: ID
    $deviceId: String
  ) {
    updateMinutesMassCreationSettings(
      meetingId: $meetingId
      minutesGroupActiveConsolidated: $minutesGroupActiveConsolidated
      minutesGroupActiveSeparate: $minutesGroupActiveSeparate
      minutesGroupReference: $minutesGroupReference
      deviceId: $deviceId
    )
  }
`;

export const updateMinutesApprovalsMutation = gql`
  mutation UpdateMinutesApprovals(
    $meetingId: ID!
    $userId: ID!
    $direntId: ID!
    $approves: Boolean!
  ) {
    updateMinutesApprovals(
      meetingId: $meetingId
      userId: $userId
      direntId: $direntId
      approves: $approves
    ) {
      id
      userId
      direntId
      createdAt
      updatedAt
    }
  }
`;

export const updateRestrictedVotersMutation = gql`
  mutation UpdateRestrictedVoters(
    $meetingId: ID!
    $userId: ID!
    $agendaItemId: ID!
    $approves: Boolean!
  ) {
    updateRestrictedVoters(
      meetingId: $meetingId
      userId: $userId
      agendaItemId: $agendaItemId
      approves: $approves
    ) {
      restrictedVoters
    }
  }
`;

export const updateAllowedVotersMutation = gql`
  mutation UpdateAllowedVoters(
    $meetingId: ID!
    $userId: ID!
    $agendaItemId: ID!
    $approves: Boolean!
  ) {
    updateAllowedVoters(
      meetingId: $meetingId
      userId: $userId
      agendaItemId: $agendaItemId
      approves: $approves
    ) {
      allowedVoters
    }
  }
`;

export const agendaItemFragment = gql`
  fragment AgendaItemFragment on AgendaItem {
    title
    agendaItemCategory
    duration
    agendaStartingIndex
    agendaResponsible
    agendaItemIsPrivate
    agendaItemOpenSpeakingList
    agendaItemIsDraft
    agendaItemHasCommentsEnabled
    iHaveSeenThisAgendaItem
    hideAgendaNumbering
    description
    proposedResolution
    electionNCandidatesToElect
    isWinnerSigningPdf
    electionCandidates {
      id
      name
      userId
    }
    voting {
      votingFor
      votingAgainst
      votingNeutral
    }
    election {
      votingFor
      value
      userId
    }

    isVotingLocked
    gaMajorityRequirementRule
    restrictedVoters
    allowedVoters
    agendaItemSuggestionBasis {
      id
    }
    agendaItemAttachedFiles
    agendaItemAttachments {
      id
      direntId
      name
      iHaveSeenThisAttachment
    }
    minuteNotes
    decisionNotes
    drawResolutionVote
    numberOfComments
  }
`;

export const meetingAgendaItemsQuery = gql`
  query MeetingAgendaItems($meetingId: ID!) {
    meeting(id: $meetingId) {
      id
      agendaItems {
        id
        ...AgendaItemFragment
      }
    }
  }
  ${agendaItemFragment}
`;

export const updateAgendaItemFieldMutation = gql`
  mutation UpdateAgendaItemField(
    $meetingId: ID!
    $agendaItemId: ID!
    $field: String!
    $newValue: String!
    $deviceId: String!
  ) {
    updateAgendaItemField(
      meetingId: $meetingId
      agendaItemId: $agendaItemId
      field: $field
      newValue: $newValue
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const updateAgendaItemVotesMutation = gql`
  mutation UpdateAgendaItemVotes(
    $meetingId: ID!
    $agendaItemId: ID!
    $voting: AgendaItemVotingInput
    $userId: ID
    $vote: String
    $deviceId: String!
  ) {
    updateAgendaItemVotes(
      meetingId: $meetingId
      agendaItemId: $agendaItemId
      voting: $voting
      userId: $userId
      vote: $vote
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const moveAgendaItemMutation = gql`
  mutation MoveAgendaItem(
    $meetingId: ID!
    $agendaItemId: ID!
    $newIndex: Int!
    $deviceId: String!
  ) {
    moveAgendaItem(
      meetingId: $meetingId
      agendaItemId: $agendaItemId
      newIndex: $newIndex
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const updateAgendaItemsFullMutation = gql`
  mutation UpdateAgendaItemsFull(
    $meetingId: ID!
    $agendaItems: [AgendaItemInput!]!
    $deviceId: String!
  ) {
    updateAgendaItemsFull(meetingId: $meetingId, agendaItems: $agendaItems, deviceId: $deviceId) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const insertAgendaItemBasedOnSuggestionMutation = gql`
  mutation insertAgendaItemBasedOnSuggestion(
    $meetingId: ID!
    $agendaItemSuggestionId: ID!
    $insertAtIndex: Int!
    $deviceId: String!
  ) {
    insertAgendaItemBasedOnSuggestion(
      meetingId: $meetingId
      agendaItemSuggestionId: $agendaItemSuggestionId
      insertAtIndex: $insertAtIndex
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const appendAgendaItemsBasedOnSuggestionsMutation = gql`
  mutation appendAgendaItemsBasedOnSuggestions(
    $meetingId: ID!
    $agendaItemSuggestionIds: [ID!]!
    $deviceId: String!
  ) {
    appendAgendaItemsBasedOnSuggestions(
      meetingId: $meetingId
      agendaItemSuggestionIds: $agendaItemSuggestionIds
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const insertAgendaItemWithData = gql`
  mutation insertAgendaItemWithData(
    $meetingId: ID!
    $input: AgendaItemInput!
    $insertAtIndex: Int
    $deviceId: String!
  ) {
    insertAgendaItemWithData(
      meetingId: $meetingId
      input: $input
      insertAtIndex: $insertAtIndex
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const appendAgendaItemsMutation = gql`
  mutation AppendAgendaItems(
    $meetingId: ID!
    $agendaItemsInput: [AgendaItemInput!]!
    $deviceId: String!
  ) {
    appendAgendaItems(
      meetingId: $meetingId
      agendaItemsInput: $agendaItemsInput
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const deleteAgendaItemMutation = gql`
  mutation deleteAgendaItem($meetingId: ID!, $agendaItemId: ID!, $deviceId: String!) {
    deleteAgendaItem(meetingId: $meetingId, agendaItemId: $agendaItemId, deviceId: $deviceId) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const updateAgendaItemFileAttachmentsMutation = gql`
  mutation UpdateAgendaItemFileAttachments(
    $meetingId: ID!
    $agendaItemId: ID!
    $newFileAttachments: [AgendaItemAttachmentInput]
    $deviceId: String!
  ) {
    updateAgendaItemFileAttachments(
      meetingId: $meetingId
      agendaItemId: $agendaItemId
      newFileAttachments: $newFileAttachments
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const updateAgendaItemVotingLockedStateMutation = gql`
  mutation UpdateAgendaItemVotingLockedState(
    $meetingId: ID!
    $agendaItemId: ID!
    $lockedState: Boolean!
    $deviceId: String!
  ) {
    updateAgendaItemVotingLockedState(
      meetingId: $meetingId
      agendaItemId: $agendaItemId
      lockedState: $lockedState
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const updateAgendaItemGAMajorityRequirementRuleMutation = gql`
  mutation UpdateAgendaItemGAMajorityRequirementRule(
    $meetingId: ID!
    $agendaItemId: ID!
    $gaMajorityRequirementRule: GAMajorityRequirementRule!
    $deviceId: String!
  ) {
    updateAgendaItemGAMajorityRequirementRule(
      meetingId: $meetingId
      agendaItemId: $agendaItemId
      gaMajorityRequirementRule: $gaMajorityRequirementRule
      deviceId: $deviceId
    ) {
      id
      ...AgendaItemFragment
    }
  }
  ${agendaItemFragment}
`;

export const agendaMinutesChangeSubscription = gql`
  subscription AgendaMinutesChange($meetingId: ID!) {
    AgendaMinutesChange(meetingId: $meetingId) {
      agendaItemId
      deviceId
      changeType
      userFullName
      currentAgendaItems {
        id
        ...AgendaItemFragment
      }
      updatedFieldInfo {
        field
        newValue
      }

      updatedVotesInfo {
        voting {
          votingFor
          votingAgainst
          votingNeutral
        }
      }
      updatedElectionInfo {
        field
        newValue {
          votingFor
          value
          userId
        }
      }
      restrictedVoters
      allowedVoters
      updatedVotingLockedState
      updatedGAMajorityRequirementRule
      minutesGroupActiveConsolidated
      minutesGroupActiveSeparate
      minutesGroupReference
      agendaItemsAppendedCount
      newGeneralCommentsValue
      minutesApprovals {
        id
        userId
        direntId
        createdAt
        updatedAt
      }
    }
  }
  ${agendaItemFragment}
`;

export const agendaItemSuggestionHelpTextQuery = gql`
  query GetAgendaItemSuggestion($agendaItemSuggestionId: ID!) {
    getAgendaItemSuggestion(id: $agendaItemSuggestionId) {
      id
      title
      helpText
    }
  }
`;
